import React, { useState } from 'react';

import {
  GetAssetDataForQuestionTitle,
  QuestionAssetData,
} from 'src/application/ContentZone/components/Widgets/_shared/Questions/hooks/useQuestionWidgetAssetProvider';

import { quizMessages } from '../../../../../../translates/quizMessages';
import { widgetMessages } from '../../../../../../translates/widgetMessages';
import { useIntlReexport } from '../../../../../hooks/useIntlReexport';
import { useOnMount } from '../../../../../hooks/useOnMount';
import { useRandom } from '../../../../../hooks/useRandom';
import { Title } from '../../../../components/common/Widget/Title';
import { TitleCaption } from '../../../../components/common/Widget/TitleCaption';
import { calculateCheckboxIcon, optionWithIdGotSelected } from './api/questionComponentStudentApi';
import { QuestionFooter } from './molecules/QuestionFooter';
import { QuestionOption } from './molecules/QuestionOption';
import { QuestionWidgetTitleImage } from './QuetionWidgetTitleImage';
import { AnswerFeedback } from '@ael/core-api';
import { StudentButton } from '@ael/shared-components-library';
import { partition } from 'lodash';

export type QuestionComponentOption = {
  text: string;
  assetData: QuestionAssetData | null;
  isCorrect: boolean;
  id: number;
};

type QuestionComponentProps = {
  questionText: string;
  onAnswered: (selectedOptionIds: Set<number>, wasCorrect: boolean) => void;
  options: QuestionComponentOption[];
  preSelectedOptionIds: Set<number>;
  hasMultipleAnswers: boolean;
  isShuffleEnabled: boolean;
  getAssetDataForQuestionTitle: GetAssetDataForQuestionTitle;
  customFeedback: AnswerFeedback;
  revealAnswers: boolean;
  repeatUntilCorrect: boolean;
};

export type SubmittedDataFail = {
  status: SubmitStatus.SubmittedAndFailed;
  correct: QuestionComponentOption[];
  incorrect: QuestionComponentOption[];
};

type NotSubmitted = {
  status: SubmitStatus.NotSubmitted;
};

type SubmittedDataPass = {
  status: SubmitStatus.SubmittedAndSucceded;
};

export type SubmittedData = SubmittedDataFail | SubmittedDataPass | NotSubmitted;

export enum SubmitStatus {
  NotSubmitted,
  SubmittedAndSucceded,
  SubmittedAndFailed,
}

export const QuestionComponent: React.FC<QuestionComponentProps> = ({
  questionText,
  onAnswered,
  options,
  preSelectedOptionIds,
  hasMultipleAnswers,
  isShuffleEnabled,
  getAssetDataForQuestionTitle,
  customFeedback,
  repeatUntilCorrect,
  revealAnswers,
}) => {
  const { formatMessage } = useIntlReexport();
  const { randomNumbers, generateNewRandomNumbers } = useRandom(options.length);

  const [areOptionsSubmitted, setAreOptionsSubmitted] = useState<boolean>(preSelectedOptionIds.size > 0);
  const [selectedOptions, setSelectedOptions] = useState(preSelectedOptionIds);

  const submittedData = React.useMemo(
    () => calculateSubmitData(areOptionsSubmitted, options, selectedOptions),
    [areOptionsSubmitted, options, selectedOptions]
  );

  const submitAnswer = () => {
    if (selectedOptions.size > 0) {
      setAreOptionsSubmitted(true);
      const submitStatus = calculateSubmitData(true, options, selectedOptions);
      onAnswered(selectedOptions, submitStatus.status === SubmitStatus.SubmittedAndSucceded);
    }
  };

  const addSelectedOption = (optionId: number) => () => {
    if (submittedData.status !== SubmitStatus.NotSubmitted) {
      return;
    }

    setSelectedOptions((prev) => {
      return optionWithIdGotSelected(optionId, prev, hasMultipleAnswers);
    });
  };

  const tryAgain = () => {
    generateNewRandomNumbers();
    setAreOptionsSubmitted(false);
    setSelectedOptions(new Set<number>());
  };

  const sortedOptions = isShuffleEnabled
    ? options
        .map((value: QuestionComponentOption, index: number) => ({ value, sort: randomNumbers[index] }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value)
    : options;

  // TODO make a proper fix, this is quick workaround.
  // if question was already answered correctly, we need to trigger the onAnswered callback on mount,
  // otherwise the parent component will not know that the question was already answered and there is a chance of getting stuck.
  useOnMount(() => {
    if (submittedData.status === SubmitStatus.SubmittedAndSucceded) {
      onAnswered(selectedOptions, submittedData.status === SubmitStatus.SubmittedAndSucceded);
    }
  });

  return (
    <>
      <TitleCaption>
        {hasMultipleAnswers
          ? formatMessage(widgetMessages.questionWidgetCaptionMultiple)
          : formatMessage(widgetMessages.questionWidgetCaption)}
      </TitleCaption>
      <Title>
        <div dangerouslySetInnerHTML={{ __html: questionText }}></div>
      </Title>
      <QuestionWidgetTitleImage assetData={getAssetDataForQuestionTitle()} />
      {sortedOptions.map((option: QuestionComponentOption, index) => {
        return (
          <QuestionOption
            key={option.id}
            assetData={option.assetData}
            iconType={calculateCheckboxIcon(
              areOptionsSubmitted,
              selectedOptions.has(option.id),
              option.isCorrect,
              hasMultipleAnswers,
              revealAnswers
            )}
            onSelect={addSelectedOption(option.id)}
            text={option.text}
            isLastOption={index === options.length - 1}
            disabled={areOptionsSubmitted}
          />
        );
      })}

      <QuestionFooter
        submittedData={submittedData}
        button={
          submittedData.status === SubmitStatus.NotSubmitted ? (
            <StudentButton onClick={submitAnswer} disabled={selectedOptions.size === 0}>
              {formatMessage(quizMessages.submit)}
            </StudentButton>
          ) : (
            <StudentButton onClick={tryAgain}>{formatMessage(quizMessages.tryAgain)}</StudentButton>
          )
        }
        customFeedback={customFeedback}
        repeatUntilCorrect={repeatUntilCorrect}
      />
    </>
  );
};

const calculateSubmitData = (
  wasSubmitted: boolean,
  options: QuestionComponentOption[],
  selectedOptionIds: Set<number>
): SubmittedData => {
  if (!wasSubmitted) {
    return { status: SubmitStatus.NotSubmitted };
  }
  const [correctlyAnswered, incorrectlyAnswered] = partition(
    options,
    (option) => option.isCorrect === selectedOptionIds.has(option.id)
  );
  const isCorrectlyAnswered = incorrectlyAnswered.length === 0;

  if (isCorrectlyAnswered) {
    return {
      status: SubmitStatus.SubmittedAndSucceded,
    };
  }
  return {
    status: SubmitStatus.SubmittedAndFailed,
    correct: correctlyAnswered,
    incorrect: incorrectlyAnswered,
  };
};
